import { Controller } from "@hotwired/stimulus"
import * as bootstrap from 'bootstrap'

export default class extends Controller {
  static targets = ["codeInput", "combinedCode", "modalContainer", "modal", "totpInput", "totpInputs"]
  static values = {
    setupUrl: String,
    modalId: { type: String, default: "setup2faModal" },
    autoOpen: { type: Boolean, default: false },
    showCancelButton: { type: Boolean, default: true },
    isRequired: { type: Boolean, default: false }
  }

  connect() {
    console.log('SetupTotp controller connected')
    this.initializeCodeInputs()

    // Auto-open modal when controller connects if autoOpen is true
    
    this.setupModal()
  }

  disconnect() {
    this.cleanupModal()
  }

  // Auto-open modal when controller connects
  async setupModal() {
    // Clean up existing modal state first
    this.cleanupModal()

    if (this.hasModalTarget) {
      const myModal = new bootstrap.Modal(document.getElementById(this.modalIdValue))
      myModal.show()
    }

    try {
      const response = await fetch(this.setupUrlValue || '/profile/setup_2fa_modal')
      const data = await response.text()

      if (this.hasModalContainerTarget) {
        this.modalContainerTarget.innerHTML = data
        
        // Initialize modal after it's loaded
        this.initializeModal()
      }

      // Show modal using Bootstrap
      if ( this.hasModalTarget) {
              if (this.showCancelButtonValue && !this.isRequiredValue) { 
        this.modal = new bootstrap.Modal(document.getElementById(this.modalIdValue))
      } else {
        this.modal = new bootstrap.Modal(document.getElementById(this.modalIdValue), {
          backdrop: 'static',
          keyboard: false
        })
      }
        console.log('modal', this.modal)
        if (this.autoOpenValue) {
          this.openModal()
        }
      }
    } catch (error) {
      console.error('Error loading 2FA setup:', error)
    }
  }

  async openModal() {
    if (this.modal) {
      this.modal.show()
    } else {
      console.log('No modal found')
    }
  }

  // Initialize modal after it's loaded via AJAX
  initializeModal() {
    console.log('initializeModal called')
    if (this.hasModalTarget) {
      console.log('Modal target in initializeModal:', this.modalTarget)
      console.log('Modal element:', this.modalTarget)
      
      // Initialize TOTP inputs
      this.initializeTOTPInputs()
      
      // Hide cancel button if opened from setup_2fa page
      this.handleCancelButtonVisibility()
    } else {
      console.log('No modal target found in initializeModal')
    }
  }

  // Handle cancel button visibility based on current page
  handleCancelButtonVisibility() {
    const cancelBtn = this.modalTarget.querySelector('.cancel-btn')
    if (cancelBtn) {
      // Hide cancel button if we're on the setup_2fa page or if TOTP is required
      if (this.showCancelButtonValue && !this.isRequiredValue) {
        cancelBtn.style.display = 'block'
      } else {
        cancelBtn.style.display = 'none'
      }
    }
  }

  // Initialize code input handling
  initializeCodeInputs() {
    this.codeInputTargets.forEach((input, index) => {
      input.addEventListener('keyup', (e) => this.handleCodeInputKeyup(e, index))
      input.addEventListener('keydown', (e) => this.handleCodeInputKeydown(e, index))
    })
  }

  // Handle keyup for code inputs
  handleCodeInputKeyup(event, index) {
    console.log('Keyup event triggered for input:', event.target)

    if (event.target.value.length === event.target.maxLength) {
      const nextInput = this.codeInputTargets[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }

    this.updateCombinedCode()
  }

  // Handle keydown for code inputs
  handleCodeInputKeydown(event, index) {
    if (event.key === 'Backspace' && event.target.value.length === 0) {
      const prevInput = this.codeInputTargets[index - 1]
      if (prevInput) {
        prevInput.focus()
      }
    }
  }

  // Update combined code value
  updateCombinedCode() {
    let code = ''
    this.codeInputTargets.forEach(input => {
      code += input.value
    })

    if (this.hasCombinedCodeTarget) {
      this.combinedCodeTarget.value = code
    }
  }

  // Initialize TOTP input handling
  initializeTOTPInputs() {
    this.codeInputTargets.forEach((input, index) => {
      input.setAttribute('data-index', index)

      input.addEventListener('input', (e) => this.handleTOTPInput(e, index))
      input.addEventListener('keydown', (e) => this.handleTOTPKeydown(e, index))
    })

    // Handle paste event for TOTP inputs
    if (this.hasTotpInputsTarget) {
      this.totpInputsTarget.addEventListener('paste', (e) => this.handleTOTPPaste(e))
    }
  }

  // Handle TOTP input
  handleTOTPInput(event, index) {
    const value = event.target.value

    if (value.length === 1) {
      // Move to next input
      if (index < 5) {
        const nextInput = this.codeInputTargets[index + 1]
        if (nextInput) {
          nextInput.focus()
        }
      }
    } else if (value.length === 0) {
      // Move to previous input on backspace
      if (index > 0) {
        const prevInput = this.codeInputTargets[index - 1]
        if (prevInput) {
          prevInput.focus()
        }
      }
    }
  }

  // Handle TOTP keydown
  handleTOTPKeydown(event, index) {
    if (event.key === 'Backspace' && event.target.value === '') {
      // Move to previous input on backspace if current input is empty
      if (index > 0) {
        const prevInput = this.codeInputTargets[index - 1]
        if (prevInput) {
          prevInput.focus()
        }
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      // Move to previous input on left arrow
      const prevInput = this.codeInputTargets[index - 1]
      if (prevInput) {
        prevInput.focus()
      }
    } else if (event.key === 'ArrowRight' && index < 5) {
      // Move to next input on right arrow
      const nextInput = this.codeInputTargets[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  // Handle TOTP paste
  handleTOTPPaste(event) {
    event.preventDefault()
    const pastedData = (event.clipboardData || window.clipboardData).getData('text')
    const digits = pastedData.replace(/\D/g, '').slice(0, 6)
    if (digits.length === 6) {
      digits.split('').forEach((digit, index) => {
        if (this.codeInputTargets[index]) {
          this.codeInputTargets[index].value = digit
        }
      })

      const lastInput = this.codeInputTargets[5]
      if (lastInput) {
        lastInput.focus()
      }
    }
  }

  async closeModal() {
    console.log('closeModal called')
    if (this.hasModalTarget) {
      this.modal.hide()
      console.log('Cleaning up modal')
    }
  }

  // Modal cleanup
  async cleanupModal() {
    if (this.hasModalTarget) {
      this.modal.hide()
      console.log('Cleaning up modal')
    }
  }

  // Modal hidden event
  modalHidden() {
    this.cleanupModal()
  }
} 